/**
 * WatchTogetherPage で使用するユーティリティ関数・定数
 */
import { Video } from "../../../types/Video";

/// YouTubeプレイヤーの状態定数
export const YT_PLAYER_STATES = {
  UNSTARTED: -1,
  ENDED: 0,
  PLAYING: 1,
  PAUSED: 2,
  BUFFERING: 3,
  CUED: 5,
} as const;

/// 時間文字列（HH:MM:SS）を秒数に変換
export const parseDurationToSeconds = (duration: string): number => {
  if (!duration || duration === "") {
    return 300; // デフォルト5分
  }

  try {
    const parts = duration.split(":");
    if (parts.length === 3) {
      // HH:MM:SS形式
      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10);
      const seconds = parseInt(parts[2], 10);
      return hours * 3600 + minutes * 60 + seconds;
    } else if (parts.length === 2) {
      // MM:SS形式
      const minutes = parseInt(parts[0], 10);
      const seconds = parseInt(parts[1], 10);
      return minutes * 60 + seconds;
    } else {
      // 数値のみの場合は秒として扱う
      const parsed = parseInt(duration, 10);
      return isNaN(parsed) ? 300 : parsed;
    }
  } catch (error) {
    console.warn("時間の解析に失敗:", duration, error);
    return 300; // デフォルト5分
  }
};

/// コメントタイムスタンプのフォーマット関数
export const formatCommentTimestamp = (timestamp: number): string => {
  const now = new Date();
  const commentDate = new Date(timestamp);

  // 今日の開始時刻（00:00:00）を取得
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // 昨日の開始時刻（00:00:00）を取得
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (commentDate >= today) {
    // 今日のコメント：時分のみ表示
    return commentDate.toLocaleTimeString("ja-JP", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } else if (commentDate >= yesterday) {
    // 昨日のコメント：「昨日 HH:MM」形式
    return `昨日 ${commentDate.toLocaleTimeString("ja-JP", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  } else {
    // 昨日以前のコメント：月日と時分を表示
    return commentDate.toLocaleString("ja-JP", {
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
};

/// Video オブジェクトを生成
export const createVideoObject = (
  detailVideo: Video,
  _serverVideo?: unknown
): Video => ({
  id: detailVideo.videoId,
  videoId: detailVideo.videoId,
  title: detailVideo.title,
  description: detailVideo.description || "",
  publishedAt: detailVideo.publishedAt || new Date().toISOString(),
  duration: detailVideo.duration,
  viewCount: detailVideo.viewCount || 0,
  likeCount: detailVideo.likeCount || 0,
  tags: detailVideo.tags || [],
  videoUrl: `https://www.youtube.com/watch?v=${detailVideo.videoId}`,
  thumbnailId: detailVideo.videoId,
  thumbnailPath: `/assets/images/thumbnails/${detailVideo.videoId}.jpg`,
});

/// ユーザー固有IDの生成（UUID v4形式）
export const generateUserId = (): string => {
  return (
    "user-" +
    "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    })
  );
};
